import { useCallback, useEffect, useState } from "react";

import { BeforeInstallPromptEvent } from "@/types/pwa";

const isStandaloneDisplay = (): boolean =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as { standalone?: boolean }).standalone === true;

const isIosDevice = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ reports as "MacIntel" but has touch support
  (window.navigator.platform === "MacIntel" &&
    window.navigator.maxTouchPoints > 1);

// macOS Safari (not iOS) — doesn't fire beforeinstallprompt either
const isMacSafari = (): boolean =>
  !isIosDevice() &&
  /^((?!chrome|android|crios|fxios).)*safari/i.test(window.navigator.userAgent);

export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return {
    canInstall: Boolean(deferredPrompt),
    isStandalone,
    isIos: isIosDevice(),
    isMacSafari: isMacSafari(),
    promptInstall,
  };
};
